import React from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { getPerformanceChartChrome, getPerformanceTooltipStyle } from '../../styles/performanceMsiStyles';
import { formatDay, formatPercent, formatRange } from './exploreFormat';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "9 Mar" inside a few months, "Mar 2022" across longer windows.
const tickFormatter = (long) => (iso) => {
    const [year, month, day] = String(iso).split('-').map(Number);
    if (!year || !month) return '';
    return long ? `${MONTHS[month - 1]} ${year}` : `${day} ${MONTHS[month - 1]}`;
};

function PathTooltip({ active, payload, style, total }) {
    const point = active ? payload?.[0]?.payload : null;
    if (!point) return null;
    return (
        <div style={style}>
            <strong>{formatDay(point.date)}</strong>
            <div>Day {point.day} of {total}</div>
            <div>So far {formatPercent(point.cumulative, 2)}</div>
            {Number.isFinite(point.daily) && <div>That day {formatPercent(point.daily, 2)}</div>}
            {Number.isFinite(point.range) && <div>Range {formatRange(point.range, 2)}</div>}
        </div>
    );
}

/**
 * The replayed result day by day, with the stock-specific range around it
 * (cumulative ± one standard deviation), so the end of the line is the cell's
 * number and the band's width at the end is its range.
 */
export default function ReplayPathChart({ points, height = 190 }) {
    const theme = useTheme();
    const chrome = getPerformanceChartChrome(theme);
    const data = points.map((point, index) => ({ ...point, day: index + 1 }));
    // Past about six months of trading days, label months.
    const long = points.length > 126;
    const end = points[points.length - 1]?.cumulative;
    return (
        <Box role="img" aria-label={`Factor replay day by day over ${points.length} trading day${points.length === 1 ? '' : 's'}, ending at ${formatPercent(end, 2)}`}
            sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke={chrome.grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={tickFormatter(long)} minTickGap={28} tick={{ fontSize: 11, fill: chrome.mutedText }} stroke={chrome.axis} />
                    <YAxis tickFormatter={(value) => formatPercent(value, 1)} width={56} tick={{ fontSize: 11, fill: chrome.mutedText }} stroke={chrome.axis} />
                    <ReferenceLine y={0} stroke={chrome.reference} />
                    <ChartTooltip content={<PathTooltip style={getPerformanceTooltipStyle(theme)} total={points.length} />} />
                    {points.some((point) => point.band) && (
                        <Area dataKey="band" stroke="none" fill={chrome.secondaryText} fillOpacity={0.16} isAnimationActive={false} />
                    )}
                    <Line dataKey="cumulative" stroke={chrome.text} strokeWidth={2} dot={points.length === 1} isAnimationActive={false} />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
}

ReplayPathChart.propTypes = {
    points: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string.isRequired,
        cumulative: PropTypes.number.isRequired,
        band: PropTypes.arrayOf(PropTypes.number),
    })).isRequired,
    height: PropTypes.number,
};
